import { Form, type UseFormReturn, useForm } from '@faceup/form'
import { defaultVariant, type FormVariant, twMerge } from '@faceup/ui'
import type { FormItemType } from '@faceup/utils'
import { type InferType, type Schema, yup } from '@faceup/validation'
import { type ReactNode, useEffect } from 'react'
import { type AFFormCommonProps, useAFFormItem } from './hooks/useAFFormItem'
import { getFieldId, useScrollToFirstError } from './hooks/useScrollToFirstError'
import type { AFFormItem } from './types'

export type { AFFormCommonProps }

type FormSubmitButtonProps =
  | { submitButtonText: 'send'; customSubmitButtonText?: never }
  | { submitButtonText?: never; customSubmitButtonText: ReactNode }

type FormItemSchema = ReturnType<ReturnType<typeof useAFFormItem>['getFormInputValueSchema']>
type FormSchema = Record<string, FormItemSchema>
type FormData = Record<string, InferType<FormItemSchema>>

type CustomField<T = unknown> = {
  id: string
  schema: Schema
  render: (form: UseFormReturn) => ReactNode
  defaultValue?: T
}

type AFFormProps = {
  items: AFFormItem[]
  onSubmit: (values: FormData) => Promise<boolean>
  defaultValues?: Partial<Record<string, InferType<FormItemSchema>>>
  excludedItemIds?: string[]
  headerFields?: CustomField[]
  footerFields?: CustomField[]
  isSubmitButtonDisabled?: boolean
  submitButtonLabel?: ReactNode
  submitErrorMessage?: ReactNode
  submitErrorMessageWrapperClassName?: string
  hideSubmitButtons?: boolean
  validationContext?: Record<string, unknown>
  renderOverrides?: Partial<
    Record<
      FormItemType,
      (args: { form: UseFormReturn; item: AFFormItem; commonProps: AFFormCommonProps }) => ReactNode
    >
  >
  buttonsPosition?: 'under' | 'under-center' | 'under-block'
  submitButtonWrapperClassName?: string
  variant?: FormVariant
  onValuesChange?: (values: Record<string, unknown>) => void
}

export const AFForm = ({
  items,
  onSubmit,
  defaultValues,
  excludedItemIds = [],
  headerFields = [],
  footerFields = [],
  isSubmitButtonDisabled = false,
  submitButtonLabel = undefined,
  submitErrorMessage,
  submitErrorMessageWrapperClassName,
  hideSubmitButtons = false,
  validationContext,
  renderOverrides,
  buttonsPosition = 'under-center',
  submitButtonWrapperClassName,
  variant = defaultVariant,
  onValuesChange,
}: AFFormProps) => {
  const sortedItems = [...items].sort((a, b) => a.order - b.order)
  const { getFormInputValueSchema, renderFormInput } = useAFFormItem()

  const itemSchema = sortedItems.reduce<FormSchema>((acc, formItem) => {
    return {
      ...acc,
      [formItem.id]: getFormInputValueSchema(formItem),
    }
  }, {})

  const customFieldsSchema = headerFields
    .concat(footerFields)
    .reduce<Record<string, Schema>>((acc, customField) => {
      return {
        ...acc,
        [customField.id]: customField.schema,
      }
    }, {})

  const schema = yup.object().shape({
    ...itemSchema,
    ...customFieldsSchema,
  })

  const itemDefaultValues = sortedItems.reduce<
    Record<string, { type: string; value: string | undefined }>
  >(
    (acc, formItem) => ({
      ...acc,
      [formItem.id]: {
        type: formItem.type,
        value: undefined,
      },
    }),
    {}
  )

  const customFieldsDefaultValues = [...headerFields, ...footerFields].reduce<
    Record<string, unknown>
  >((acc, customField) => {
    return {
      ...acc,
      [customField.id]: customField.defaultValue,
    }
  }, {})

  const form = useForm({
    schema,
    afterSubmit: 'persistValues',
    defaultValues: {
      ...(itemDefaultValues as InferType<typeof schema>),
      ...customFieldsDefaultValues,
      ...(defaultValues as InferType<typeof schema>),
    },
    context: validationContext,
  })

  useEffect(() => {
    if (!onValuesChange) {
      return
    }
    const subscription = form.watch(values => onValuesChange(values))
    return () => subscription.unsubscribe()
  }, [form, onValuesChange])

  const itemsToRender = sortedItems.filter(item => {
    if (renderOverrides?.[item.type]) {
      return true
    }
    return !excludedItemIds.includes(item.id)
  })

  const { submitCount, errors } = form.formState

  useScrollToFirstError({
    submitCount,
    errors,
    orderedFieldIds: [
      ...headerFields.map(f => f.id),
      ...itemsToRender.map(item => item.id),
      ...footerFields.map(f => f.id),
    ],
  })

  const submitButtonProps: FormSubmitButtonProps =
    submitButtonLabel === undefined
      ? {
          submitButtonText: 'send',
        }
      : { customSubmitButtonText: submitButtonLabel }

  return (
    <Form
      variant={variant}
      buttonsPosition={buttonsPosition}
      submitButtonWrapperClassName={submitButtonWrapperClassName}
      hideSubmitButtons={hideSubmitButtons}
      {...submitButtonProps}
      form={form}
      isSuccessMessageDisplayed={false}
      isUnsavedAlertDisabled={true}
      isSubmitButtonDisabled={isSubmitButtonDisabled}
      onSubmit={onSubmit}
    >
      <div className='flex flex-col gap-6'>
        {headerFields.map(customField => (
          <div key={customField.id} id={getFieldId(customField.id)}>
            {customField.render(form)}
          </div>
        ))}
        {itemsToRender.map(item => {
          const commonProps: AFFormCommonProps = {
            name: `${item.id}.value`,
            label: item.label,
            hint: item.hint ?? undefined,
            disabled: false,
            withAsterisk: item.isRequired,
            control: form.control,
          }
          const renderOverride = renderOverrides?.[item.type]
          return (
            <div key={item.id} id={getFieldId(item.id)}>
              {renderOverride
                ? renderOverride({ form, item, commonProps })
                : renderFormInput(form, item)}
            </div>
          )
        })}
        {footerFields.map(customField => (
          <div key={customField.id} id={getFieldId(customField.id)}>
            {customField.render(form)}
          </div>
        ))}
        {submitErrorMessage && submitCount > 0 && Object.keys(errors).length > 0 && (
          <output
            className={twMerge(
              'block text-center text-colorError',
              submitErrorMessageWrapperClassName
            )}
          >
            {submitErrorMessage}
          </output>
        )}
      </div>
    </Form>
  )
}
